import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Lock, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { PaginationState } from '@tanstack/react-table';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';
import { AppDataTable } from '../../shared/table/AppDataTable.tsx';
import type { TableColumnDef } from '../../shared/table/types.ts';
import { useDebouncedValue } from '../../shared/hooks/useDebouncedValue.ts';
import { getApiErrorMessage } from '../../request/getApiErrorMessage.ts';
import {
  downloadHygieneExport,
  type HygieneArea,
  type HygieneFinding,
  type HygieneScan,
  type HygieneSeverity,
  type HygieneTab,
} from '../../request/hygiene/hygieneRequests.ts';
import {
  isScanActive,
  useHygieneAssignees,
  useHygieneChecks,
  useHygieneFindings,
  useHygieneScanStatus,
  useHygieneSummary,
  useStartHygieneScan,
} from '../../hooks/hygiene/useHygiene.ts';
import { AREA_LABELS, SEVERITY_LABELS, SEVERITY_PILL, STATE_LABELS, ageLabel, findingLink, formatDate } from './labels.ts';
import { HygieneSummaryBar } from './components/HygieneSummaryBar.tsx';
import { FindingPanel } from './components/FindingPanel.tsx';
import { AIReviewStartDialog, AIReviewStatus } from './components/AIReviewPanel.tsx';

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

const COUNT_LABELS: Array<[string, string]> = [
  ['processes', 'processes'],
  ['risks', 'risks'],
  ['controls', 'controls'],
  ['systems', 'systems'],
  ['test_runs', 'test runs'],
  ['issues', 'issues'],
];

function checkedLine(counts: Record<string, number>): string {
  return COUNT_LABELS.filter(([k]) => counts[k] !== undefined)
    .map(([k, label]) => `${counts[k]} ${label}`)
    .join(', ');
}

/**
 * Hygiene Check (Readiness): what must be fixed before testing or the external auditor arrives, who owns it, and
 * how close the audit year is to ready. Findings are stored by the backend; opening the page reads them and asks
 * for a fresh rule check when the year is stale.
 */
export function HygieneCheckPage() {
  const { selectedAuditYearId } = useAuditYear();
  const yearId = selectedAuditYearId ?? null;

  const [tab, setTab] = useState<HygieneTab>('open');
  const [severity, setSeverity] = useState<HygieneSeverity | ''>('');
  const [area, setArea] = useState<HygieneArea | ''>('');
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim());
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [openId, setOpenId] = useState<string | null>(null);
  const [aiDialog, setAiDialog] = useState(false);
  const [activeAIScan, setActiveAIScan] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // A different tab or filter starts again at page 1. The setters reset the page in the same render, so the list is
  // fetched once (an effect would first fetch the new filter on the old page). Search is debounced, so it resets here.
  const firstPage = useCallback(() => setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 })), []);
  const changeTab = (t: HygieneTab) => { setTab(t); firstPage(); };
  const changeSeverity = (v: HygieneSeverity | '') => { setSeverity(v); firstPage(); };
  const changeArea = (v: HygieneArea | '') => { setArea(v); firstPage(); };
  useEffect(() => {
    firstPage();
  }, [yearId, query, firstPage]);
  useEffect(() => {
    setOpenId(null);
    setActiveAIScan(null);
    setTab('open');
    setSeverity('');
    setArea('');
    setSearch('');
  }, [yearId]);

  const summaryQuery = useHygieneSummary(yearId);
  const summaryData = summaryQuery.data;
  const summary = summaryData?.summary;
  const perms = summaryData?.permissions;
  const locked = Boolean(summaryData?.audit_year.is_locked);
  const checksQuery = useHygieneChecks();
  const checks = useMemo(() => new Map((checksQuery.data ?? []).map((c) => [c.code, c])), [checksQuery.data]);
  const membersQuery = useHygieneAssignees(Boolean(yearId));
  const members = useMemo(() => new Map((membersQuery.data ?? []).map((m) => [m.id, m])), [membersQuery.data]);
  const membersLoading = membersQuery.isLoading;

  const listParams = useMemo(
    () =>
      yearId
        ? { audit_year_id: yearId, tab, severity, area, q: query, page: pagination.pageIndex + 1, page_size: pagination.pageSize }
        : null,
    [yearId, tab, severity, area, query, pagination]
  );
  const findingsQuery = useHygieneFindings(listParams);

  // After the last item of a page is fixed or accepted, step back to the last page that still has items.
  const total = findingsQuery.data?.total;
  useEffect(() => {
    if (total === undefined || findingsQuery.isFetching) return;
    const lastPage = Math.max(0, Math.ceil(total / pagination.pageSize) - 1);
    if (pagination.pageIndex > lastPage) setPagination((p) => ({ ...p, pageIndex: lastPage }));
  }, [total, findingsQuery.isFetching, pagination.pageIndex, pagination.pageSize]);

  // Runs that finished while this page followed them. The summary can still list one as active until it refetches,
  // so it must not be picked up again (that would repeat the "finished" toast and keep the button disabled).
  const finishedAIScans = useRef<Set<string>>(new Set());

  const startScan = useStartHygieneScan(
    useCallback(
      (scan: HygieneScan) => {
        if (scan.kind === 'ai' && scan.audit_year_id === yearId) setActiveAIScan(scan.id);
      },
      [yearId]
    )
  );

  // Follow an AI review that is already running (started elsewhere, or before a reload).
  const runningAI = summary?.active_scans.find((s) => s.kind === 'ai' && !finishedAIScans.current.has(s.id));
  useEffect(() => {
    if (runningAI && !activeAIScan && !summaryQuery.isFetching) setActiveAIScan(runningAI.id);
  }, [runningAI, activeAIScan, summaryQuery.isFetching]);

  const aiStatus = useHygieneScanStatus(activeAIScan, yearId);
  const liveAI = aiStatus.data?.scan;
  const aiStalled = Boolean(aiStatus.data?.stalled);
  const aiRunning = Boolean(liveAI && isScanActive(liveAI) && !aiStalled) || (Boolean(activeAIScan) && !liveAI);

  // When the followed run ends, say how it ended once, then stop following it.
  useEffect(() => {
    if (!liveAI || isScanActive(liveAI) || finishedAIScans.current.has(liveAI.id)) return;
    finishedAIScans.current.add(liveAI.id);
    setActiveAIScan(null);
    if (liveAI.status === 'completed') toast.success('The AI review finished. New suggestions are in the AI review tab.');
    else if (liveAI.status === 'cancelled') toast('The AI review was cancelled. Finished items were kept.');
    else toast.error(liveAI.error_message || 'The AI review failed.');
  }, [liveAI]);

  // Opening the page refreshes a stale year once (the backend runs the rule check in seconds).
  const autoChecked = useRef<Set<string>>(new Set());
  const rulesRunning = summary?.active_scans.some((s) => s.kind === 'rules') ?? false;
  useEffect(() => {
    if (!yearId || !summary || !perms?.can_run_checks || !summary.stale || rulesRunning || startScan.isPending) return;
    if (autoChecked.current.has(yearId)) return;
    autoChecked.current.add(yearId);
    startScan.mutate({ auditYearId: yearId, kind: 'rules' });
  }, [yearId, summary, perms, rulesRunning, startScan]);

  const exportFile = async (format: 'csv' | 'xlsx') => {
    if (!yearId) return;
    setExporting(true);
    try {
      const year = summaryData?.audit_year.year;
      const { blob, filename } = await downloadHygieneExport({ audit_year_id: yearId, tab, severity, area, q: query }, format, year ? `hygiene-check-FY${year}` : 'hygiene-check');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking right away can cancel the download in some browsers (Safari).
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'The export could not be created.'));
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<TableColumnDef<HygieneFinding>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        widthPercent: 11,
        kind: 'custom',
        cell: ({ row }) => {
          const f = row.original;
          if (f.state === 'suggested') {
            return (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 ring-1 ring-blue-200">
                <Sparkles className="h-3 w-3" aria-hidden /> {f.ai_verdict === 'unsure' ? 'Unsure' : 'AI review'}
              </span>
            );
          }
          return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${SEVERITY_PILL[f.severity]}`}>{SEVERITY_LABELS[f.severity]}</span>;
        },
      },
      {
        id: 'check',
        header: 'Check',
        widthPercent: 26,
        kind: 'custom',
        cell: ({ row }) => {
          const f = row.original;
          const meta = checks.get(f.check);
          // A real button, so keyboard users can open the item (the row itself is mouse-only).
          return (
            <button type="button" onClick={() => setOpenId(f.id)} className="block w-full min-w-0 rounded text-left focus:outline-none focus:ring-2 focus:ring-blue-500">
              <span className="line-clamp-1 break-words font-medium text-gray-900" title={meta?.title}>{meta?.title ?? f.check}</span>
              <span className="line-clamp-2 break-words text-xs text-gray-500" title={f.detail}>{f.detail}</span>
            </button>
          );
        },
      },
      {
        id: 'record',
        header: 'Record',
        widthPercent: 24,
        kind: 'custom',
        cell: ({ row }) => {
          const link = findingLink(row.original);
          return link ? (
            <Link to={link.to} state={link.state} onClick={(e) => { e.stopPropagation(); window.scrollTo(0, 0); }} className="line-clamp-2 break-words text-blue-700 hover:underline" title={row.original.record_label}>
              {row.original.record_label}
            </Link>
          ) : (
            <span className="line-clamp-2 break-words text-gray-800" title={row.original.record_label}>{row.original.record_label}</span>
          );
        },
      },
      {
        id: 'area',
        header: 'Area',
        widthPercent: 9,
        kind: 'custom',
        cell: ({ row }) => <span className="text-gray-700">{AREA_LABELS[row.original.area] ?? row.original.area}</span>,
      },
      {
        id: 'owner',
        header: 'Owner',
        widthPercent: 14,
        kind: 'custom',
        cell: ({ row }) => {
          const f = row.original;
          if (f.owner_user_id && membersLoading) return <span className="text-gray-400">…</span>;
          // Inactive users are not in the members list: fall back to the record's owner name, then the id.
          const name = f.owner_user_id ? members.get(f.owner_user_id)?.name || f.owner_label || `User ${f.owner_user_id}` : f.owner_label;
          return <span className={`line-clamp-1 break-words ${name ? 'text-gray-800' : 'text-gray-400'}`}>{name || 'Unassigned'}</span>;
        },
      },
      {
        id: 'when',
        header: tab === 'fixed' ? 'Fixed' : 'Open for',
        widthPercent: 9,
        kind: 'custom',
        cell: ({ row }) => (
          <span className="text-gray-600">{tab === 'fixed' ? formatDate(row.original.resolved_at) : ageLabel(row.original.first_seen_at)}</span>
        ),
      },
      {
        id: 'state',
        header: 'State',
        widthPercent: 7,
        kind: 'custom',
        cell: ({ row }) => {
          const f = row.original;
          return (
            <span className="text-xs text-gray-600">
              {STATE_LABELS[f.state]}
              {f.acceptance_expired && f.state !== 'accepted' ? <span className="block text-amber-700">acceptance expired</span> : null}
            </span>
          );
        },
      },
    ],
    [checks, members, membersLoading, tab]
  );

  if (!yearId) {
    return <div className="p-6 text-sm text-gray-600">Choose an audit year in the sidebar to see its hygiene check.</div>;
  }

  const counts = summary?.checked_counts ?? {};
  const lastRules = summary?.last_rules_scan;
  const nothingToCheck = Object.values(counts).every((v) => !v);
  const openTotal = (summary?.high ?? 0) + (summary?.medium ?? 0) + (summary?.low ?? 0);
  const tabs: Array<{ id: HygieneTab; label: string; count?: number; show: boolean }> = [
    { id: 'open', label: 'All open', count: openTotal, show: true },
    { id: 'mine', label: 'Assigned to me', count: summary?.assigned_to_me, show: true },
    { id: 'ai', label: 'AI review', count: summary?.ai_suggestions, show: true },
    { id: 'approval', label: 'Waiting for approval', count: summary?.pending_approval, show: Boolean(summary?.pending_approval) || Boolean(perms?.can_approve) },
    { id: 'accepted', label: 'Accepted', count: summary?.accepted, show: true },
    { id: 'fixed', label: 'Fixed', count: summary?.fixed_total, show: true },
  ];

  const filtered = Boolean(severity || area || query);
  const EMPTY_BY_TAB: Record<HygieneTab, string> = {
    open: nothingToCheck && lastRules
      ? 'Nothing to check yet: add processes, risks or controls for this audit year.'
      : `No open items${checkedLine(counts) ? ` across ${checkedLine(counts)}` : ''}.`,
    mine: 'Nothing is assigned to you.',
    ai: 'No AI suggestions to review.',
    approval: 'Nothing is waiting for approval.',
    accepted: 'No accepted items.',
    fixed: 'Nothing has been fixed yet.',
  };
  const emptyMessage = filtered ? 'No items match these filters.' : EMPTY_BY_TAB[tab];
  const staleForViewer = Boolean(summary?.stale && lastRules && !perms?.can_run_checks && !locked);

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Hygiene Check{summaryData ? ` · FY ${summaryData.audit_year.year}` : ''}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {startScan.isPending && startScan.variables?.kind === 'rules'
              ? 'Updating…'
              : lastRules
                ? `Rules checked ${formatDate(lastRules.finished_at ?? lastRules.started_at)}${checkedLine(counts) ? ` · ${checkedLine(counts)}` : ''}`
                : 'Not checked yet.'}
            {staleForViewer ? <span className="text-amber-700"> · records changed since; ask an internal auditor to re-check</span> : null}
            {lastRules && Object.keys(lastRules.failed_checks ?? {}).length ? (
              <span className="text-amber-700"> · {Object.keys(lastRules.failed_checks).length} check(s) could not run</span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {perms?.can_manage ? (
            <button
              type="button"
              disabled={aiRunning || startScan.isPending}
              onClick={() => setAiDialog(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-900 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" aria-hidden /> Run AI review
            </button>
          ) : null}
          {perms?.can_run_checks ? (
            <button
              type="button"
              disabled={startScan.isPending}
              onClick={() => startScan.mutate({ auditYearId: yearId, kind: 'rules' })}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${startScan.isPending && startScan.variables?.kind === 'rules' ? 'animate-spin' : ''}`} aria-hidden /> Re-check
            </button>
          ) : null}
          <button
            type="button"
            disabled={exporting}
            onClick={() => exportFile('xlsx')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" aria-hidden /> Export Excel
          </button>
          <button
            type="button"
            disabled={exporting}
            onClick={() => exportFile('csv')}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            CSV
          </button>
        </div>
      </div>

      {locked ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <Lock className="h-4 w-4" aria-hidden /> FY {summaryData?.audit_year.year} is locked — read only. Items cannot be assigned or accepted.
        </div>
      ) : null}

      {summaryQuery.isError ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {getApiErrorMessage(summaryQuery.error, 'The hygiene check could not be loaded.')}
          <button type="button" onClick={() => summaryQuery.refetch()} className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium">
            Retry
          </button>
        </div>
      ) : null}

      {summary ? (
        <HygieneSummaryBar
          summary={summary}
          severity={severity}
          area={area}
          onSeverity={(v) => {
            changeSeverity(v);
            if (tab !== 'open' && tab !== 'mine') setTab('open');
          }}
          onArea={changeArea}
          aiSelected={tab === 'ai'}
          onAITiles={() => changeTab('ai')}
        />
      ) : summaryQuery.isLoading ? (
        <div className="h-40 animate-pulse rounded-xl bg-gray-100" />
      ) : null}

      {summary && (liveAI ?? summary.last_ai_scan) ? (
        <AIReviewStatus
          scan={(liveAI ?? summary.last_ai_scan) as HygieneScan}
          stalled={aiStalled}
          pollError={aiStatus.isError}
          checks={checks}
          canManage={Boolean(perms?.can_manage)}
        />
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-5" aria-label="Hygiene item lists">
          {tabs
            .filter((t) => t.show)
            .map((t) => (
              <button
                key={t.id}
                type="button"
                aria-current={tab === t.id ? 'page' : undefined}
                onClick={() => changeTab(t.id)}
                className={`border-b-2 px-0.5 pb-2 text-sm ${tab === t.id ? 'border-blue-600 font-semibold text-gray-900' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              >
                {t.label}
                {t.count ? <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">{t.count}</span> : null}
              </button>
            ))}
        </nav>
        <div className="mb-2 flex items-center gap-2">
          {severity || area || search ? (
            <button type="button" onClick={() => { changeSeverity(''); changeArea(''); setSearch(''); }} className="text-xs text-blue-700 hover:underline">
              Clear filters{severity ? ` · ${SEVERITY_LABELS[severity]}` : ''}{area ? ` · ${AREA_LABELS[area]}` : ''}{search ? ' · search' : ''}
            </button>
          ) : null}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search records…"
            aria-label="Search hygiene items"
            className="w-56 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className={findingsQuery.isFetching && !findingsQuery.isLoading ? 'opacity-60 transition-opacity' : 'transition-opacity'} aria-busy={findingsQuery.isFetching}>
      <AppDataTable<HygieneFinding>
        data={findingsQuery.data?.items ?? []}
        columns={columns}
        mode="server"
        loading={findingsQuery.isLoading}
        error={findingsQuery.isError ? getApiErrorMessage(findingsQuery.error, 'Hygiene items could not be loaded.') : null}
        emptyMessage={emptyMessage}
        paginated
        paginationItemLabel="items"
        showPageSizeSelector
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        pagination={pagination}
        onPaginationChange={setPagination}
        rowCount={findingsQuery.data?.total ?? 0}
        getRowId={(row) => row.id}
        onRowClick={(row) => setOpenId(row.id)}
        isRowSelected={(row) => row.id === openId}
      />
      </div>

      {openId ? (
        <FindingPanel
          findingId={openId}
          auditYearId={yearId}
          checks={checks}
          members={members}
          canManage={Boolean(perms?.can_manage)}
          canApprove={Boolean(perms?.can_approve)}
          currentUserId={summaryData?.user_id}
          onClose={() => setOpenId(null)}
        />
      ) : null}

      {aiDialog ? (
        <AIReviewStartDialog
          auditYearId={yearId}
          checks={checks}
          busy={startScan.isPending}
          onClose={() => setAiDialog(false)}
          onConfirm={() =>
            startScan.mutate({ auditYearId: yearId, kind: 'ai' }, { onSettled: () => setAiDialog(false) })
          }
        />
      ) : null}
    </div>
  );
}
